import React from "react";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import cookie from "react-cookies";
import {
	arrayToObject,
	countMap,
	forEachObj,
	isEmpty,
	lowerFirst,
	mapObject,
	mapObjectName,
	objectMerge,
	saveUserToken,
	upperFirst
} from "./lib/tools";
import {apiCall, apiCallAsync, getUserData, getUserId} from "./lib/api";

//<editor-fold desc="Wrapper logic">
class ReduxWrapper extends React.Component {
	loadPromises = {};
	loadAwaits = {};
	
	componentWillMount() {
		forEachObj(this.props.loads, (x, k) => {
			this.loadPromises[k] = x();
			this.loadAwaits["await" + upperFirst(k)] = f => this.loadPromises[k].then(f);
		});
		const awaitUserData = getUserToken(this.props.state, this.props.dispatch).then(token => getUserData(token));
		this.loadAwaits["awaitUserData"] = f => awaitUserData.then(f);
	}
	
	render() {
		const child = this.props.component;
		const {calls, values, loads, state, dispatch, ...props} = this.props;
		if (values.hasOwnProperty("apiCall")) values.apiCall = values.apiCall(dispatch);
		return React.createElement(child, {...objectMerge(calls, props), ...values, ...this.loadAwaits});
	}
}

function handleRule(keyword, group, values, calls, loads) {
	const action = keyword + upperFirst(group);
	if (customActions.value.hasOwnProperty(action))
		values[group] = customActions.value[action];
	if (customActions.calls.hasOwnProperty(action))
		calls[group] = customActions.calls[action];
	
	if (!rules.hasOwnProperty(group)) return;
	
	const rule = rules[group];
	
	switch (keyword) {
		// All
		case "load":
			if (rule.hasOwnProperty("endpoint"))
				loads[group] = (dispatch, props, args) => dispatch({standard: true, group, args, type: keyword});
			values[group] = (state, props) => state[group];
			
			const dependencies = rule.dependencies || [];
			dependencies.forEach(x => !loads.hasOwnProperty(x) && handleRule("load", x, values, calls, loads));
			break;
		
		case "invalidate":
		case "set":
		case "add":
		case "delete":
		case "update":
			calls[action] = (dispatch, props, args) => dispatch({
				standard: true,
				group,
				args,
				type: keyword
			});
			break;
		default:
			break;
	}
}

export function reduxWrapper(/**/) {
	const values = {};
	const calls = {};
	const loads = {};
	
	for (let key in arguments) {
		if (!arguments.hasOwnProperty(key)) continue;
		const arg = arguments[key];
		
		const match = arg.match(/([a-z]+)([A-Z].*)/);
		if (!match) continue;
		
		const keyword = match[1];
		const group = lowerFirst(match[2].trim());
		
		handleRule(keyword, group, values, calls, loads);
	}
	
	return component => withRouter(connect(
		(state, props) => ({
			state,
			values: mapObject(values, x => x(state, props), true),
			component
		}),
		(dispatch, props) => ({
			dispatch,
			loads: mapObject(loads, x => (args) => x(dispatch, props, args)),
			calls: mapObject(calls, x => (args) => x(dispatch, props, args))
		})
	)(ReduxWrapper));
}

//</editor-fold>

//<editor-fold desc="Data rules">

/*
Action list:

LOAD
INVALIDATE
SET
ADD list
DELETE list
UPDATE list
 */

const rules = {
	// User data
	userToken: {
		list: false,
		reset: null,
		default: null
	},
	partyInfo: {
		dependencies: ["userToken"],
		list: false,
		endpoint: "/party/current"
	},
	userInfo: {
		dependencies: ["userToken"],
		list: false,
		endpoint: "/user/me"
	},
	cardConfig: {
		dependencies: ["partyInfo"],
		list: false,
		endpoint: "/party/current/cards"
	},
	guests: {
		dependencies: ["partyInfo"],
		endpoint: "/party/current/guests",
		unitEndpoint: "/party/current/guest/{id}"
	},
	guestTables: {
		dependencies: ["partyInfo", "guests"],
		endpoint: "/party/current/guests/tables",
		unitEndpoint: "/party/current/guests/table/{id}"
	},
	cart: {
		dependencies: ["partyInfo"],
		endpoint: "/party/current/cart",
		unitEndpoint: "/party/current/cart/item/{id}"
	},
	todo: {
		dependencies: ["partyInfo"],
		endpoint: "/party/current/todo",
		unitEndpoint: "/party/current/todo/item/{id}"
	},
	entries: {
		dependencies: [],
		endpoint: "/competition/entries",
		unitEndpoint: "/competition/entry/{id}"
	},
	
	// Static data
	attributes: {
		endpoint: "configuration/attributes",
		mapProp: "name",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	options: {
		endpoint: "configuration/options",
		mapProp: "name",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	occasions: {
		endpoint: "configuration/occasions",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	sections: {
		endpoint: "configuration/sections",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	productTypes: {
		endpoint: "configuration/productTypes",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	customOptions: {
		endpoint: "configuration/customOptions",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	vendors: {
		endpoint: "vendors",
		adminCreate: true,
		adminDelete: true,
		adminUpdate: true
	},
	
	// Product cache
	products: {},
	
	// Error
	error: {
		reset: null,
		default: null,
		list: false
	}
};

function getParents(group) {
	const groups = [];
	forEachObj(rules, (x, k) => {
		if (x.hasOwnProperty("dependencies") && x.dependencies.includes(group)) {
			groups.push(k);
			const children = getParents(k);
			children.forEach(y => !groups.includes(y) && groups.push(y));
		}
	});
	return groups;
}

//</editor-fold>

//<editor-fold desc="Default State">
const defaultState = (() => {
	const ret = mapObject(rules, (x, k) => ({key: k, data: x.hasOwnProperty("default") ? x.default : {}}));
	ret.loaded = {error: true, userToken: rules.userToken.default != null};
	return ret;
})();
//</editor-fold>

//<editor-fold desc="Custom rules">
export const getApiCall = (state, dispatch) =>
	async (method, call, successCallback, postData, errorCallback, files) => {
		const token = await getUserToken(state, dispatch);
		return apiCall(token, method, call, successCallback, postData, errorCallback, files);
	};

const customActions = {
	value: {
		getApiCall: (state, props) => dispatch => getApiCall(state, dispatch),
		getUserId: (state, props) => getUserId(state.userToken),
		getLoadStatus: (state, props) => ({
			max: countMap(state.loaded, x => x != null),
			current: countMap(state.loaded, x => x === true)
		}),
		getPartyExists: (state, props) => state.loaded.partyInfo === null ? false : (state.loaded.partyInfo === true ? !isEmpty(state.partyInfo) : null)
	},
	calls: {
		getLogin: (dispatch, props, args) => {
			dispatch({standard: true, group: "userToken", type: "login", args});
		},
		getFacebookLogin: (dispatch, props, args) => {
			window.FB.login(response => {
				if (response.authResponse)
					dispatch({
						standard: true,
						group: "userToken",
						type: "fbLogin",
						accessToken: response.authResponse.accessToken,
						args
					});
			}, {scope: "email"});
		},
		getEmailLogin: (dispatch, props, args) => dispatch({standard: true, group: "userToken", type: "email", args}),
		getRegister: (dispatch, props, args) => dispatch({standard: true, group: "userToken", type: "register", args}),
		getLogOut: (dispatch, props, args) => dispatch({standard: true, group: "userToken", type: "guestLogin", args}),
		getCreateParty: (dispatch, props, args) => dispatch({standard: true, group: "partyInfo", type: "create", args}),
		getSelectParty: (dispatch, props, args) => dispatch({standard: true, group: "partyInfo", type: "select", args}),
		getTransferParties: (dispatch, props, args) => dispatch({
			standard: true,
			group: "partyInfo",
			type: "transfer",
			args
		}),
		getImportGuests: (dispatch, props, args) => dispatch({standard: true, group: "guests", type: "import", args}),
		getAddGuestToTable: (dispatch, props, args) => dispatch({
			standard: true,
			group: "guestTables",
			type: "addGuest",
			args
		}),
		getRemoveGuestFromTable: (dispatch, props, args) => dispatch({
			standard: true,
			group: "guestTables",
			type: "removeGuest",
			args
		}),
		getCacheProduct: (dispatch, props, args) => dispatch({standard: true, group: "products", type: "cache", args}),
		getCacheProductManual: (dispatch, props, args) => dispatch({standard: true, group: "products", type: "merge", args}),
		getFetchProducts: (dispatch, props, args) => dispatch({
			standard: true,
			group: "products",
			type: "storeFetch",
			args
		}),
		getAddMultipleToCart: (dispatch, props, args) => dispatch({standard: true, group: "cart", type: "addMultiple", args}),
		getCheckoutCart: (dispatch, props, args) => dispatch({standard: true, group: "cart", type: "checkout", args}),
		getClearCart: (dispatch, props, args) => dispatch({standard: true, group: "cart", type: "clear", args}),
		getPayOrder: (dispatch, props, args) => dispatch({standard: true, group: "cart", type: "pay", args}),
		getEntryVote: (dispatch, props, args) => dispatch({standard: true, group: "entries", type: "vote", args: {positive: true, ...args}}),
		getEntryDownvote: (dispatch, props, args) => dispatch({standard: true, group: "entries", type: "vote", args: {positive: false, ...args}}),
	}
};

const customMiddleware = {
	userToken: {
		login: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/login", action,
				({token, oldParties}) => {
					saveUserToken(token);
					if (!oldParties)
						store.dispatch({standard: true, type: "invalidate", group: "userToken", ignoreGroup: true});
				});
		},
		register: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/register", {...action, type: "login"},
				({token, oldParties}) => {
					saveUserToken(token);
					if (!oldParties)
						store.dispatch({standard: true, type: "invalidate", group: "userToken", ignoreGroup: true});
				});
		},
		fbLogin: (store, next, action) => {
			const accessToken = action.accessToken;
			return middlewareApiCall(store, "POST", "/login/facebook", {...action, type: "login", args: {...action.args, data: {accessToken}}},
				({token, oldParties}) => {
					saveUserToken(token);
					if (!oldParties)
						store.dispatch({standard: true, type: "invalidate", group: "userToken", ignoreGroup: true});
				});
		},
		guestLogin: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/login/guest", {...action, type: "login"},
				({token}) => {
					saveUserToken(token);
					store.dispatch({standard: true, type: "invalidate", group: "userToken", ignoreGroup: true});
				});
		},
		email: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/login/first", {...action, type: "login"},
				({token}) => {
					saveUserToken(token);
					store.dispatch({standard: true, type: "invalidate", group: "userToken", ignoreGroup: true});
				});
		},
	},
	partyInfo: {
		create: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/parties", {...action, type: "set"},
				data => store.dispatch({standard: true, type: "invalidate", group: "partyInfo"}));
		},
		transfer: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/parties/transfer", {...action, type: "invalidate"});
		},
		select: (store, next, action) => {
			return middlewareApiCall(store, "PUT", `/party/${action.args.id}/select`, {...action, type: "invalidate"});
		},
	},
	guests: {
		import: (store, next, action) => {
			return middlewareApiCall(store, "POST", "/party/current/guests/list", {
					...action,
					type: "load",
					args: {files: {guests: action.args.file}}
				},
				data => store.dispatch({standard: true, type: "invalidate", group: "guests", ignoreGroup: true}));
		}
	},
	guestTables: {
		addGuest: (store, next, action) => {
			return middlewareApiCall(store, "POST", `/party/current/guests/table/${action.args.table}/guest/${action.args.guest}`, action);
		},
		removeGuest: (store, next, action) => {
			return middlewareApiCall(store, "DELETE", `/party/current/guests/table/${action.args.table}/guest/${action.args.guest}`, action);
		}
	},
	products: {
		cache: (store, next, action) => {
			const simple = !action.args.full;
			const state = store.getState();
			const ids = [...(action.args.products || []), ...(action.args.product ? [action.args.product] : [])]
				.filter(x => !(
					(simple && state.products.hasOwnProperty(x)) ||
					(!simple && state.products.hasOwnProperty(x) && state.products[x].full)
				));
			if (ids.length < 1) return;
			
			const placeholder = ids.map(x => ({id: x}));
			next({group: "products", type: "merge", data: placeholder});
			
			return middlewareApiCall(store, "GET", "/products", {
				...action,
				type: "merge",
				args: {...action.args, data: {ids, simple: simple ? 1 : 0}}
			});
		},
		storeFetch: (store, next, action) => {
			const args = action.args || {};
			return middlewareApiCall(store, "GET", `/subsection/${args.section}/products`,
				{...action, type: "ignore"}, data => {
					const state = store.getState();
					store.dispatch({
						group: "products",
						type: "merge",
						data: data.filter(x => !state.products.hasOwnProperty(x.id))
					});
					args.afterLoad && args.afterLoad(data);
				});
		}
	},
	cart: {
		checkout: (store, next, action) => {
			const args = action.args || {};
			return middlewareApiCall(store, "POST", "party/current/checkout", {...action, args: {...args, data: {...args.data, affiliate: cookie.load("affiliateCode") || null}}, showError: true, type: "ignore"});
		},
		pay: (store, next, action) => {
			const args = action.args || {};
			return middlewareApiCall(store, "POST", `/order/${args.order}/pay`, {
				...action,
				showError: true,
				type: "invalidate"
			});
		},
		addMultiple: (store, next, action) => {
			const args = action.args || {};
			return middlewareApiCall(store, "POST", "party/current/cart/multiple", {...action, args: {...args, data: {products: args.products}}, showError: true, type: "merge"});
		},
		clear: (store, next, action) => {
			return middlewareApiCall(store, "DELETE", "party/current/cart", action);
		},
	},
	entries: {
		vote: (store, next, action) => {
			const args = action.args || {};
			console.log(args);
			return middlewareApiCall(store, "POST", `competition/entry/${args.id}/vote`, {
				...action,
				args: {id: parseInt(args.id, 10), data: {positive: args.positive}},
				showError: true,
				type: "update"
			});
		}
	}
};

const customReducers = {
	userToken: {
		login: (state, action) => action.data.token
	},
	guestTables: {
		addGuest: (state, action) => mapObject(state, x => {
			if (x.id === parseInt(action.args.table, 10))
				return objectMerge(x, {guests: [...x.guests, action.args.guest]});
			else
				return objectMerge(x, {guests: x.guests.filter(y => parseInt(y, 10) !== parseInt(action.args.guest, 10))});
		}),
		removeGuest: (state, action) => {
			const newState = {...state};
			if (newState[action.args.table])
				newState[action.args.table].guests = newState[action.args.table].guests.filter(x => parseInt(x, 10) !== parseInt(action.args.guest, 10));
			return newState;
		}
	},
	cart: {
		clear: (state, action) => {
			return {};
		}
	}
};
//</editor-fold>

//<editor-fold desc="Redux Middleware">
let loadingUserToken = null;

async function getUserToken(state, dispatch) {
	const userToken = state.userToken;
	
	if (!userToken) {
		if (!loadingUserToken) {
			loadingUserToken = new Promise((resolve, reject) => {
				const cookieToken = cookie.load("userToken");
				if (cookieToken) {
					apiCallAsync(cookieToken, "POST", "/token/extend")
						.then(({token}) => token ? resolve(token) : resolve(cookieToken))
						.catch(() => {
							apiCallAsync(null, "POST", "/login/guest")
								.then(({token}) => token ? resolve(token) : reject("No token returned"))
								.catch(e => reject(e))
						});
				} else {
					apiCallAsync(null, "POST", "/login/guest")
						.then(({token}) => token ? resolve(token) : reject("No token returned"))
						.catch(e => reject(e))
				}
			}).catch(e => {
				throw e;
			}).then(token => {
				saveUserToken(token);
				dispatch({group: "userToken", type: "load", data: token});
				return token;
			});
		}
		
		return await loadingUserToken;
	} else {
		return userToken;
	}
}

async function middlewareApiCall(store, method, endpoint, action, afterSuccess) {
	const userToken = await getUserToken(store.getState(), store.dispatch);
	const args = action.args || {};
	const postData = args.data || {};
	const rule = rules[action.group] || {};
	
	try {
		const data = await apiCallAsync(userToken, method, endpoint, postData, args.files);
		
		if (action.wait !== false && action.type !== "ignore") {
			store.dispatch({
				standard: action.type === "invalidate",
				group: action.group,
				type: action.type,
				data,
				postData,
				id: args.id || null,
				args
			});
		}
		
		afterSuccess && afterSuccess(data);
		
		args.success && args.success(data, store.dispatch);
		args.onSuccess && args.onSuccess(data, store.dispatch);
		
		return store.getState()[action.group];
		
	} catch (e) {
		if (action.type === "load")
			store.dispatch({type: "failedLoad", group: action.group});
		
		if (args.error || args.onError) {
			args.error && args.error(e.message, store.dispatch);
			args.onError && args.onError(e.message, store.dispatch);
		} else if (e.display) {
			if (rule.showError || args.showError) {
				store.dispatch(showError({message: e.message}));
			} else {
				store.dispatch(showError({message: "Ups... Der gik noget galt. Prøv igen eller kontakt vores kundeservice hvis fejlen fortsætter"}));
			}
		}
		
		return rule.default || defaultState[action.group];
	}
}

export const globalStateMiddleware = store => next => action => {
	if (action.standard !== true)
		return next(action);
	
	if (!action.group || !action.type) return;
	
	const state = store.getState();
	const args = action.args || {};
	const group = action.group;
	const type = action.type;
	const rule = rules[group];
	const id = args.id || null;
	
	switch (type) {
		// All
		case "load":
			if (state.loaded[group] === true) break;
			if (state.loaded[group]) return state.loaded[group];
			if (!rule.hasOwnProperty("endpoint")) break;
			if (rule.hasOwnProperty("default")) break;
			
			const promise = middlewareApiCall(store, "GET", (rule.adminLoad ? "admin/" : "") + rule.endpoint, action);
			next({type: "startLoad", group, promise});
			return promise;
		
		case "invalidate":
			const groups = args.ignoreGroup || action.ignoreGroup ? getParents(group) : [group, ...getParents(group)];
			const newGroups = groups.filter(x => state.loaded.hasOwnProperty(x));
			next({type: "invalidate", groups: newGroups});
			newGroups.forEach(x => store.dispatch({standard: true, group: x, type: "load"}));
			break;
		
		case "set":
			if (rule.list !== false) break;
			if (!rule.hasOwnProperty("endpoint") || args.wait !== true) {
				next({type: "set", group, id, data: args.data});
				action.wait = false;
			}
			if (rule.hasOwnProperty("endpoint")) {
				return middlewareApiCall(store, "PUT", (rule.adminUpdate ? "admin/" : "") + rule.endpoint, action);
			}
			break;
		
		case "add":
			if (!rule.hasOwnProperty("endpoint")) break;
			return middlewareApiCall(store, "POST", (rule.adminCreate ? "admin/" : "") + rule.endpoint, action);
		
		case "delete":
			if (!id) break;
			if (!rule.hasOwnProperty("unitEndpoint") || args.wait !== true) {
				next({type: "delete", group, id});
				action.wait = false;
			}
			if (rule.hasOwnProperty("unitEndpoint")) {
				const endpoint = rule.unitEndpoint.replace("{id}", id);
				return middlewareApiCall(store, "DELETE", (rule.adminDelete ? "admin/" : "") + endpoint, action);
			}
			break;
		
		case "update":
			if (!id) break;
			if (!rule.hasOwnProperty("unitEndpoint") || args.wait !== true) {
				next({type: "update", group, id, data: args.data});
				action.wait = false;
			}
			if (rule.hasOwnProperty("unitEndpoint")) {
				const endpoint = rule.unitEndpoint.replace("{id}", id);
				return middlewareApiCall(store, "PUT", (rule.adminUpdate ? "admin/" : "") + endpoint, action);
			}
			break;
		
		default:
			if (customMiddleware.hasOwnProperty(group)) {
				if (customMiddleware[group].hasOwnProperty(type)) {
					const prom = customMiddleware[group][type](store, next, action);
					if (prom) return prom;
					return new Promise((resolve) => resolve(state[group]));
				}
			}
			console.warn(`The redux keyword '${action.type}' is not supported`);
	}
	return new Promise((resolve) => resolve(state[group]));
};

let errorTimeout;

export const errorMiddleware = store => next => action => {
	if (action.group === "error" && action.type === "set") {
		clearTimeout(errorTimeout);
		errorTimeout = setTimeout(() => store.dispatch({groups: ["error"], type: "invalidate"}), 4000);
	}
	
	return next(action);
};

const showError = ({message}) => ({
	group: "error",
	type: "set",
	data: message
});
//</editor-fold>

//<editor-fold desc="Reducer">
export function reducer(state, action) {
	if (state == null)
		return defaultState;
	
	if (action.type === "startLoad") {
		return {...state, loaded: {...state.loaded, [action.group]: action.promise}}
	}
	
	if (action.type === "failedLoad") {
		const newState = {...state};
		newState[action.group] = rules[action.group].hasOwnProperty("reset") ? rules[action.group].reset : {};
		newState.loaded[action.group] = null;
		return newState;
	}
	
	if (action.type === "invalidate") {
		const newState = {...state};
		action.groups.forEach(x => {
			newState[x] = rules[x].hasOwnProperty("reset") ? rules[x].reset : {};
			delete newState.loaded[x];
		});
		return newState;
	}
	
	const loaded = state.loaded;
	if (action.type === "load") {
		loaded[action.group] = true;
	}
	
	return mapObject(state, (val, key) => {
		if (key === "loaded")
			return loaded;
		if (action.group === key)
			return reduceGroup(action.group, val, action);
		return val;
	});
}

function reduceGroup(group, state, action) {
	const rule = rules[group];
	const type = action.type;
	let newState;
	
	switch (type) {
		case "load":
			if (rule.list === false)
				return action.data;
			if (rule.mapProp)
				return mapObjectName(action.data, rule.mapProp);
			return arrayToObject(action.data);
		case "set":
			if (typeof action.data === "object")
				return objectMerge(state, {...action.postData, ...action.data});
			return action.data;
		case "add":
			if (rule.mapProp)
				return {...state, [action.data.id]: action.data[rule.mapProp] || action.postData[rule.mapProp]};
			return {...state, [action.data.id]: {...action.postData, ...action.data}};
		case "delete":
			newState = {...state};
			delete newState[action.id];
			return newState;
		case "update":
			if (rule.mapProp)
				return {...state, [action.id]: action.data[rule.mapProp] || action.postData[rule.mapProp]};
			return {...state, [action.id]: {...state[action.id], ...action.postData, ...action.data}};
		case "merge":
			if (rule.list === false)
				return objectMerge(state, arrayToObject(action.postData), arrayToObject(action.data));
			if (rule.mapProp)
				return objectMerge(state, mapObjectName(action.data, rule.mapProp));
			newState = {...state};
			const data = arrayToObject(action.data);
			forEachObj(data, (x, k) => newState[k] = objectMerge(state[k], x));
			return newState;
		default:
			return customReducers[group][type](state, action);
	}
}

//</editor-fold>


// WEBPACK FOOTER //
// ./src/globalState.js